import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  DoubleMaterialityStakeholderType,
  LocalizationLanguage,
  StakeholderDto,
} from "@green0meter/api-client";
import { Edit as EditIcon } from "@mui/icons-material";

import StakeholderEditorActions from "./components/StakeholderEditorActions";
import ImpactAreasField from "./components/ImpactAreasField/ImpactAreasField";
import ESRSAreasField from "./components/ESRSAreasField/ESRSAreasField";
import StakeholderTypeField from "./components/StakeholderTypeField";

import { Drawer } from "@components/common";
import DatePickerField from "@components/common/DatePickerField";
import LabeledControllerField from "@components/common/LabeledControllerField/LabeledControllerField";
import {
  Stack,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Switch,
} from "@components/base";
import { PLATFORM_LANGUAGES } from "@constants";

interface StakeholdersEditorProps {
  open: boolean;
  onClose: () => void;
  stakeholder: StakeholderDto | null;
}

export type StakeholderFormValues = {
  fullName: string;
  email: string;
  deadline: string | undefined | null;
  companyName: string;
  comment?: string;
  language: LocalizationLanguage;
  type: StakeholderDto["type"];
  impactAreas: StakeholderDto["impactAreas"];
  esrsAreas: StakeholderDto["esrsAreas"];
  emailReminders: boolean;
};

const validationSchema = yup.object().shape({
  fullName: yup.string().required("Full name is required"),
  email: yup.string().email("Invalid email format").required("Email is required"),
  deadline: yup.string().required().nullable(),
  companyName: yup.string().required("Company name is required"),
  comment: yup.string().optional(),
  language: yup.number().required("Language is required"),
  type: yup.mixed<StakeholderDto["type"]>().required("Type is required"),
  impactAreas: yup
    .array()
    .of(yup.mixed<StakeholderDto["impactAreas"][number]>().required())
    .required()
    .min(1, "At least one impact area is required"),
  esrsAreas: yup
    .array()
    .of(yup.mixed<StakeholderDto["esrsAreas"][number]>().required())
    .required()
    .min(1, "At least one ESRS area is required"),
  emailReminders: yup.boolean().default(false),
});

const StakeholdersEditor: FC<StakeholdersEditorProps> = ({
  open,
  onClose,
  stakeholder,
}) => {
  const { t } = useTranslation();
  const [isPendingOperation, setIsPendingOperation] = useState(false);

  const form = useForm<StakeholderFormValues>({
    // @ts-expect-error yupResolver type mismatch with react-hook-form@7
    resolver: yupResolver<StakeholderFormValues>(validationSchema),
    mode: "onChange",
    defaultValues: {
      fullName: "",
      email: "",
      companyName: "",
      deadline: null,
      comment: "",
      language: LocalizationLanguage.EN,
      type: DoubleMaterialityStakeholderType.External,
      impactAreas: [],
      emailReminders: false,
      esrsAreas: [],
    },
  });

  const { control, reset } = form;

  useEffect(() => {
    if (!open) return;

    if (stakeholder) {
      reset({
        fullName: stakeholder.fullName,
        email: stakeholder.email,
        companyName: stakeholder.companyName,
        comment: stakeholder.comment,
        deadline: stakeholder.deadline,
        language: stakeholder.language,
        type: stakeholder.type,
        impactAreas: stakeholder.impactAreas,
        esrsAreas: stakeholder.esrsAreas,
        emailReminders: stakeholder.emailReminders,
      });
    } else {
      // Reset to default values for create mode
      reset({
        fullName: "",
        email: "",
        companyName: "",
        comment: "",
        deadline: null,
        language: LocalizationLanguage.EN,
        type: DoubleMaterialityStakeholderType.External,
        impactAreas: [],
        esrsAreas: [],
        emailReminders: false,
      });
    }
  }, [stakeholder, open, reset]);

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const content = (
    <Stack spacing={3} sx={{ height: "100%" }}>
      <Stack spacing={2} sx={{ flex: 1 }}>
        <ImpactAreasField control={control} disabled={isPendingOperation} />

        <ESRSAreasField control={control} disabled={isPendingOperation} />

        <StakeholderTypeField control={control} disabled={isPendingOperation} />

        <LabeledControllerField
          label={t("stakeholders.fields.deadline")}
          name="deadline"
          control={control}
          disabled={isPendingOperation}
          render={({ disabled }) => (
            <DatePickerField
              control={control}
              name={"deadline"}
              disabled={disabled}
            />
          )}
        />

        <LabeledControllerField
          label={t("stakeholders.fields.emailReminders")}
          name="emailReminders"
          control={control}
          disabled={isPendingOperation}
          render={({ field, disabled }) => (
            <Switch {...field} checked={Boolean(field.value)} disabled={disabled} />
          )}
        />

        <LabeledControllerField
          label={"Full name"}
          name="fullName"
          control={control}
          disabled={isPendingOperation}
          render={({ field, error, disabled }) => (
            <TextField
              {...field}
              error={Boolean(error)}
              helperText={error?.message}
              disabled={disabled}
              fullWidth
              variant="filled"
            />
          )}
        />

        <LabeledControllerField
          label={t("stakeholders.fields.email", "E-mail")}
          name="email"
          control={control}
          disabled={isPendingOperation}
          render={({ field, error, disabled }) => (
            <TextField
              {...field}
              type="email"
              error={Boolean(error)}
              helperText={error?.message}
              disabled={disabled}
              fullWidth
              variant="filled"
            />
          )}
        />

        <LabeledControllerField
          label={t("stakeholders.fields.companyName", "Company name")}
          name="companyName"
          control={control}
          disabled={isPendingOperation}
          render={({ field, error, disabled }) => (
            <TextField
              {...field}
              error={Boolean(error)}
              helperText={error?.message}
              disabled={disabled}
              fullWidth
              variant="filled"
            />
          )}
        />

        <LabeledControllerField
          label={t("stakeholders.fields.language", "Language")}
          name="language"
          control={control}
          disabled={isPendingOperation}
          render={({ field, error, disabled }) => (
            <FormControl fullWidth error={Boolean(error)}>
              <Select {...field} disabled={disabled} variant="filled">
                {PLATFORM_LANGUAGES.map((language) => (
                  <MenuItem key={language.id} value={language.id}>
                    {language.name}
                  </MenuItem>
                ))}
              </Select>
              {error && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.75 }}
                >
                  {error.message}
                </Typography>
              )}
            </FormControl>
          )}
        />

        <LabeledControllerField
          label={t("stakeholders.fields.comment", "Comment")}
          name="comment"
          control={control}
          disabled={isPendingOperation}
          render={({ field, error, disabled }) => (
            <TextField
              {...field}
              multiline
              rows={3}
              error={Boolean(error)}
              helperText={error?.message}
              disabled={disabled}
              fullWidth
              variant="filled"
            />
          )}
        />
      </Stack>

      <StakeholderEditorActions
        handleClose={handleClose}
        form={form}
        stakeholder={stakeholder}
        onDeleteSuccess={handleClose}
        onUpdateSuccess={handleClose}
        onCreateSuccess={handleClose}
        onPendingChange={setIsPendingOperation}
      />
    </Stack>
  );

  const contentWithHeader = (
    <Stack spacing={3} sx={{ height: "100%" }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <EditIcon sx={{ color: "text.secondary" }} />
        <Typography variant="h6" color="text.primary">
          {stakeholder
            ? t("stakeholders.editor.title_edit", "EDIT STAKEHOLDER")
            : t("stakeholders.editor.title_create", "CREATE STAKEHOLDER")}
        </Typography>
      </Stack>
      {content}
    </Stack>
  );

  return (
    <Drawer open={open} onClose={handleClose} title="" content={contentWithHeader} />
  );
};

export default StakeholdersEditor;
